import {BRAND_PALETTE, paletteSelectionOf, type BrandPaletteColorId} from "../../account/brand/brandPalette";
import {previewBackdropArt, type BackdropArt} from "../../sponsor/takeover/backdropArt";
import {isRenderedTemplate, type RenderedTemplateKey} from "../../sponsor/sponsorBooking";
import {artStage, wallpaperLine, type BackdropStage} from "../../download/pageBackdrop";
import {splitTransferMessage} from "../../download/transferMessage";
import {personalizedSenderName} from "../../download/recipientPersonalization";
import {deriveSenderIdentity, type SenderIdentity} from "../../download/senderIdentity";
import {companyLines, senderLines, type CompanyFields, type SenderChoice} from "sender/senderChoice";
import {DOWNLOAD_ROUTE_SEGMENT, FILE_REQUEST_ROUTE_SEGMENT} from "../../attribution/adAttribution";
import {displayedOrigin} from "../../nav/displayedOrigin";
import {recipientFontStack, type PersonalizationFontKey} from "./personalizationFont";
import {
	NO_INHERITED_PERSONALIZATION, personalizationNote, type PersonalizationDraftState, type PersonalizationInherited,
} from "./personalizationDraft";
import {effectivePersonalizationAccent} from "./personalizationAccent";

/**
 * What the builder's miniature recipient page shows, derived from the draft (ui#1921).
 *
 * React-free by mandate (epic #26) — but the reason to extract it is not the mandate. The builder now
 * claims to be a LIVE preview, and that claim is only true if every editable facet reaches the
 * miniature. That is a mapping, and a mapping is exactly the kind of thing that quietly loses a field
 * during a restyle: this repo keeps no UI-component tests, so nothing else would notice.
 *
 * Eight rules live here rather than in the view, and each is a rule about the RECIPIENT's page rather
 * than about markup:
 *
 *   1. INHERITANCE. A draft field left alone inherits the account's own branding
 *      (`personalizationFieldOrigin`), so the miniature has to show the inherited value or it previews
 *      a page nobody will get. That holds for the download page; a request's upload page inherits no look
 *      from the account ({@link previewInheritance}).
 *   2. THE FOLD. The builder collects Title and Message separately and the wire carries one field, so
 *      the miniature folds and re-splits through the same two functions the wire and the recipient page
 *      use — never by reading the two draft fields straight into a heading and a body.
 *   3. THE ART. Which pictures animate, and how many, is `previewBackdropArt`'s arithmetic — the same
 *      answer the builder's own picker tiles are drawn from, so the tile and the miniature can never
 *      disagree about what a look does with three pictures.
 *   4. THE PROMISE ON THE BUTTON. "Download all" over a single file is a plural claim about one thing,
 *      so the caption follows the count ({@link PreviewDownloadLabelKey}) — and the files head above it
 *      quotes the same {@link PersonalizationPreviewModel.fileCount}, so the two cannot disagree.
 *   5. WHO THE PAGE SAYS IT IS FROM. An untyped display name does not mean an anonymous page — see
 *      {@link previewSenderName}.
 *   6. THE STAGE AND THE LINE (owner, 2026-09-03). A backdrop composes beside the payload box and
 *      carries no line; a wallpaper keeps the whole page and typesets the sender's own line on it.
 *      Both are read through `download/pageBackdrop.ts`'s rules — `artStage`, `wallpaperLine` — which
 *      are the recipient page's own, so the miniature cannot put a look on a stage the page would not.
 *   7. WHICH PAGE (kitecorp/transferify-ui#3173). A transfer's recipient opens the download page and a
 *      request's contact opens the upload page, so the miniature draws the page its reader will open
 *      ({@link previewPageFor}), with that page's route in its bar ({@link previewAddress}).
 *   8. THE UPLOAD PAGE'S CARD. Who it names, the look's message, the request's note and when its
 *      countdown ends, as the api and that page decide them ({@link uploadPagePreview}).
 */

/**
 * The size the miniature's single file row stands for, in bytes (2.4 GB).
 *
 * The miniature is an impression of the PAGE, not a rendering of the transfer (owner, 2026-09-03: "one
 * file row"), so the row stands for a file rather than reporting one — the builder is opened long
 * before the transfer's own list is anything the recipient will see. A constant here rather than a
 * literal in the view because the row and the files head above it both quote it, and two literals would
 * eventually quote two figures.
 */
export const PREVIEW_SAMPLE_FILE_BYTES = 2_576_980_378;

/**
 * Which `download.all` caption the miniature's pill wears (owner decision on ui#1921).
 *
 * A KEY rather than a string, the same arrangement `download/redownload.DownloadAllLabelKey` uses for
 * the recipient page's own button: the rule picks which caption applies, the catalogue owns its wording
 * in the reader's language.
 *
 * Its own union rather than that one, because the two ladders answer different questions. The page's
 * asks what a click would DO (zip on the server, decrypt locally, retry, wait for the sender); this asks
 * only how many files the promise covers. Widening the page's union with a case its resolver never
 * returns would be a value no caller there can produce.
 */
export type PreviewDownloadLabelKey = "downloadOne" | "downloadAll";

export interface PersonalizationPreviewModel {
	/** The takeover art to mount, or `null` when the page carries no look at all. */
	readonly art: BackdropArt | null;
	/**
	 * Where {@link art} composes, in the recipient page's own terms: a `showcase` backdrop retreats to
	 * the region beside the payload box, a `wallpaper` keeps the whole page, and `none` opens no region.
	 */
	readonly stage: BackdropStage;
	/**
	 * The sender's line typeset on their wallpaper, or `null` when there is nothing to typeset — a
	 * backdrop carries no line, and a blank is nothing rather than an empty block.
	 */
	readonly posterLine: string | null;
	readonly logoUrl: string | null;
	/**
	 * The name THIS TRANSFER carries, or `null` when the sender has typed nothing worth sending.
	 *
	 * Not yet who the page says it is from: an untyped name is the ordinary case and the page is not
	 * anonymous for it. That fallback is {@link previewSenderName}, kept apart from this mapping because
	 * it needs the account — session state the draft does not hold.
	 */
	readonly senderName: string | null;
	/** The heading the recipient page would promote out of the message, if any. */
	readonly heading: string | null;
	/** Everything else of the message, already trimmed. */
	readonly body: string;
	/**
	 * The title and the message folded as the wire carries them, whole: the upload page shows the look's words
	 * as one block under its own heading (kitecorp/transferify-ui#3173), where the download page splits them.
	 */
	readonly lookMessage: string;
	/** The CSS font stack the miniature paints its copy in. */
	readonly fontStack: string;
	/** The accent hex, or `null` to leave the miniature on the app's own colour. */
	readonly accentColor: string | null;
	/** The look in force, for the one-line summary. */
	readonly template: RenderedTemplateKey | null;
	/** The face in force, for the one-line summary. `null` is the standard face. */
	readonly font: PersonalizationFontKey | null;
	/**
	 * The palette entry the accent corresponds to, for naming it in the summary.
	 *
	 * Resolved against the WHOLE palette rather than the five the builder offers: an inherited accent is
	 * routinely one of the other 31, and the summary describes what the recipient sees rather than what
	 * this control could have produced.
	 */
	readonly colorId: BrandPaletteColorId | null;
	/**
	 * How many files the recipient's page would list, never below one.
	 *
	 * The miniature still draws a single stand-in ROW (owner, 2026-09-03) — the builder opens before the
	 * send and a list of real names is not what a sender is looking at here — but the head above that row
	 * quotes this, so the count the page states and the promise its button makes come from one number.
	 */
	readonly fileCount: number;
	/** See {@link PreviewDownloadLabelKey}. */
	readonly downloadLabelKey: PreviewDownloadLabelKey;
}

export interface PersonalizationPreviewInputs {
	/** The stand-in pictures a look animates before the sender has uploaded any. */
	readonly placeholderArtUrls: readonly string[];
	/** The face the page uses when the sender has chosen none. */
	readonly standardFontStack: string;
	/**
	 * How many files this transfer is carrying right now.
	 *
	 * Zero is a real value and not a caller's mistake: the builder outlives its selection (a send-another
	 * clears it while the sheet is still mounted), so the model floors it rather than refusing it — a
	 * page with no files at all is not a state the recipient can reach.
	 */
	readonly fileCount: number;
}

/**
 * The signed-in account as the recipient page would be told about it: its name, and the address the
 * transfer is sent from. Both nullable because both states are real — an account may carry no name,
 * and the builder renders for a visitor the session knows nothing about.
 */
export interface PreviewSenderAccount {
	readonly name: string | null;
	readonly email: string | null;
}

/**
 * Who the recipient's page says the transfer is from, or `null` when nothing names this sender (ui#1952).
 *
 * An untyped display name is the ORDINARY case — most senders never open the name control — and the
 * page they get is not anonymous: the api discloses the account's own name, and the page falls from
 * that to the address the transfer was sent from. A miniature that showed nothing there was previewing
 * a page no recipient would ever see.
 *
 * Composed through the recipient page's own two functions rather than restated, so the precedence
 * cannot drift: `personalizedSenderName` is the page's rule that this transfer's name displaces the
 * account's, and `deriveSenderIdentity` is its rule for falling from a name to an address and for what
 * `null` means. The stand-in personalization carries the ONE field the first of those reads, because at
 * this point in the flow the draft IS the transfer's personalization — nothing else about it bears on
 * who the page says it is from, and filling in the other four from a draft would invent facts.
 *
 * @param typedName {@link PersonalizationPreviewModel.senderName}, or the raw field: whitespace counts as
 *   nothing typed, the way the model already reads it.
 */
export function previewSenderName(typedName: string | null, account: PreviewSenderAccount): string | null {
	const displayName = typedName === null || typedName.trim() === "" ? null : typedName.trim();
	return deriveSenderIdentity(
		personalizedSenderName({art: null, displayName, message: null, brand: null, font: null}, account.name),
		account.email,
	).display;
}

/**
 * Who a transfer's download page says it is from, for a transfer sent as `company` (kitecorp/transferify-ui#3186):
 * the company, with its tax ID on the line below when it has one (`companyLines`, owner 2026-10-02), as the page
 * names it. The look's name is not sent for a company, so it plays no part. Until the company has a name the
 * account's stands in, with no tax ID line, as it would on one that cannot be sent yet anyway; then the address, as
 * {@link previewSenderName} falls back. A request's upload page names its sender through {@link uploadPagePreview}.
 */
export function previewCompanySender(
	account: PreviewSenderAccount,
	company: CompanyFields,
	taxIdLine: (id: string) => string,
): {name: string | null; taxId: string | null} {
	const lines = companyLines(company.name, company.id, taxIdLine);
	return {
		name: deriveSenderIdentity(lines?.name ?? account.name, account.email).display,
		taxId: lines?.taxId ?? null,
	};
}

/**
 * The page the miniature draws (kitecorp/transferify-ui#3173, owner 2026-10-02): the download page a transfer's
 * recipient opens, or, while the panel asks for files, the upload page a request's contact opens. A request's
 * contact never sees a download page, so previewing one there showed the sender a page nobody would open.
 */
export type PreviewPage = "download" | "upload";

/** Which page the miniature draws: the upload page while the panel asks for files, else the download page. */
export function previewPageFor(askingForFiles: boolean): PreviewPage {
	return askingForFiles ? "upload" : "download";
}

/**
 * The look the page inherits from the account. The download page paints the account's own look under the
 * transfer's, because the api sends the account's brand and theme with every transfer; the upload page paints
 * the request's own look alone, because the api serves a request's page no other look
 * (`ReminderUpload.personalization`). So the upload page inherits no look, and its miniature neither. (The one
 * account fact the upload page does show is the owner's profile picture in its avatar, which the session does not
 * carry; the miniature shows the initial there.)
 */
export function previewInheritance(page: PreviewPage, inherited: PersonalizationInherited): PersonalizationInherited {
	return page === "upload" ? NO_INHERITED_PERSONALIZATION : inherited;
}

/**
 * Each page's own route prefix (`routes.tsx`: `download/:id`, `reminder-uploads/:reminderId`), with the id elided.
 * The segments are the ones every other reader of these routes takes (`attribution/adAttribution`), so the bar
 * cannot name a route the site no longer serves.
 *
 * The id is elided rather than invented because there is no transfer or request yet: the builder is open
 * before either exists. What the bar can say truthfully is the site the reader will open and the path they will
 * open it at, which is what makes the frame a frame rather than a decoration.
 */
const PAGE_PATHS: Readonly<Record<PreviewPage, string>> = {
	download: `/${DOWNLOAD_ROUTE_SEGMENT}/`,
	upload: `/${FILE_REQUEST_ROUTE_SEGMENT}/`,
};

/**
 * The address the miniature's browser bar shows, or `null` before the browser has said where it is.
 *
 * The frame is what makes the picture read as a PAGE rather than as a panel in the sheet, and an
 * address bar with nothing in it is a frame that has forgotten what it frames. Built from the origin the
 * sender is actually on (`displayedOrigin`, the ui#1517 rule) rather than a brand constant, so a sender
 * on the development site is not shown a production address.
 *
 * @param currentOrigin from `utils/useMountedOrigin` — `""` during the SSG pre-render and on the first
 *   client render, which is exactly the "no host signal" `displayedOrigin` answers `null` for.
 */
export function previewAddress(currentOrigin: string, page: PreviewPage = "download"): string | null {
	const origin = displayedOrigin(currentOrigin);
	return origin === null ? null : `${new URL(origin).host}${PAGE_PATHS[page]}…`;
}

/** The account as a request's sender line reads it: its name, and its company for a company request. */
export interface UploadPreviewAccount {
	readonly name: string | null;
	readonly company: string;
}

/** The request being composed, as far as its upload page shows it. */
export interface RequestPagePreviewInputs {
	/** Who the request is sent as: a person, or a company with its name and tax ID. */
	readonly sender: SenderChoice;
	/** What the contact is asked for, as typed. */
	readonly note: string;
	/**
	 * How many days the request takes files, or null when it never closes. The api counts them from when a link
	 * request is made, and from each email an emailed request sends (`Reminder.reopenFrom`), not from when the
	 * contact opens the page.
	 */
	readonly openForDays: number | null;
}

/** What the request composer reports about the request for its upload page's miniature: its note and period. */
export type RequestPageDraft = Pick<RequestPagePreviewInputs, "note" | "openForDays">;

/** What the upload page's card shows, beside the look's art, logo and accent the shared model carries. */
export interface UploadPagePreview {
	/** Who the page names as the request's sender, and the initial its avatar shows. */
	readonly sender: SenderIdentity;
	/** A company's tax ID line, which the page shows under the sender's name (owner 2026-10-02), or null. */
	readonly senderTaxId: string | null;
	/** The look's own title and message, whole, as the page shows them under its heading; "" when it has none. */
	readonly lookMessage: string;
	/** The request's note, what the contact is asked for, or null until one is typed. */
	readonly note: string | null;
	/** When the page's countdown ends, in epoch milliseconds, or null when the request never closes. */
	readonly closesAtMs: number | null;
}

/** A day, as the api adds one to a request's period (`OffsetDateTime.plusDays` keeps the offset, so 24 hours). */
const DAY_MS = 86_400_000;

/**
 * A name the api shows a request's contact, as `RecipientFacingNames.usable` decides it, trimmed: present, not
 * blank, and with no `@` anywhere in it. Its own rule rather than `deriveSenderIdentity`'s `name`, which only
 * refuses a name with something on both sides of its `@`: the api skips "Ana @ Studio" too.
 */
function recipientFacingName(name: string | null): string | null {
	const trimmed = name?.trim() ?? "";
	return trimmed === "" || trimmed.includes("@") ? null : trimmed;
}

/**
 * What the request's upload page shows in its card (kitecorp/transferify-ui#3173), read off the miniature's model.
 *
 * THE SENDER is named as the api names it (`Reminder.sender`) and the page reads it (`senderLines`): a person by
 * the look's name, else the account's; a company by its own name, with its tax ID on the line under it (owner
 * 2026-10-02), else by the account's company, else the account's name. The api skips an account or company name it
 * may not show a contact ({@link recipientFacingName}), but serves the look's name as typed, so the page shows that
 * whatever it reads as, and so does the miniature. A company request carries no look name, because the composer
 * leaves it out (`sender/senderChoice.sentAs`). The page is not told the owner's address, so with no usable name it
 * names nobody rather than falling back to one.
 *
 * THE LOOK'S MESSAGE is the one the request carries, shown whole as the page shows it, since the page's own
 * heading takes the heading's place.
 *
 * THE COUNTDOWN ends the whole period after `drawnAtMs`, the moment the builder drew the card: that is what the
 * page reads when a link request is made or its email goes out, which is when its period starts.
 */
export function uploadPagePreview(
	preview: Pick<PersonalizationPreviewModel, "senderName" | "lookMessage">,
	account: UploadPreviewAccount,
	{sender, note, openForDays}: RequestPagePreviewInputs,
	taxIdLine: (id: string) => string,
	drawnAtMs: number,
): UploadPagePreview {
	const ownCompany = sender.kind === "company" ? recipientFacingName(sender.company.name) : null;
	const named = sender.kind === "person"
		? {name: recipientFacingName(account.name), companyId: null, lookName: preview.senderName}
		: {
			name: ownCompany ?? recipientFacingName(account.company) ?? recipientFacingName(account.name),
			companyId: ownCompany === null ? null : sender.company.id.trim() || null,
			lookName: null,
		};
	const lines = senderLines(named, taxIdLine);
	const trimmedNote = note.trim();
	return {
		sender: deriveSenderIdentity(lines?.name ?? null, null),
		senderTaxId: lines?.taxId ?? null,
		lookMessage: preview.lookMessage,
		note: trimmedNote === "" ? null : trimmedNote,
		closesAtMs: openForDays === null ? null : drawnAtMs + openForDays * DAY_MS,
	};
}

export function personalizationPreview(
	draft: PersonalizationDraftState,
	inherited: PersonalizationInherited,
	{placeholderArtUrls, standardFontStack, fileCount}: PersonalizationPreviewInputs,
): PersonalizationPreviewModel {
	const files = Math.max(1, Math.floor(fileCount));
	const chosen = draft.template ?? inherited.template;
	// A look the api stores but this bundle cannot draw yet (a Halloween scene before its painter,
	// kitecorp/transferify-ui#3776) previews as the plain page, which is what the recipient page will show.
	const template: RenderedTemplateKey | null = chosen !== null && isRenderedTemplate(chosen) ? chosen : null;
	const chosenImageUrls = draft.images.map(image => image.url);
	const imageUrls = chosenImageUrls.length > 0 ? chosenImageUrls : inherited.imageUrls;
	const accentColor = effectivePersonalizationAccent(draft, inherited);
	const lookMessage = personalizationNote(draft);
	const {heading, body} = splitTransferMessage(lookMessage);
	const art = template === null
		? null
		: previewBackdropArt(template, imageUrls, placeholderArtUrls, draft.options);

	return {
		art,
		stage: artStage(art),
		posterLine: wallpaperLine(art),
		logoUrl: draft.logo?.url ?? inherited.logoUrl,
		senderName: draft.displayName.trim() === "" ? null : draft.displayName.trim(),
		heading,
		body,
		lookMessage,
		fontStack: recipientFontStack(draft.font, standardFontStack),
		accentColor,
		template,
		font: draft.font,
		colorId: paletteSelectionOf(BRAND_PALETTE, accentColor),
		fileCount: files,
		downloadLabelKey: files === 1 ? "downloadOne" : "downloadAll",
	};
}
